import {type StructureResolver} from 'sanity/structure'
import {RiImageAddLine} from 'react-icons/ri'
import {Iframe} from 'sanity-plugin-iframe-pane'
import resolveProductionUrl from '../resolveProductionUrl'

// Document views with plugins
const defaultDocumentViews = (S: any) => [
  S.view.form(),
  S.view
    .component(Iframe)
    .options({
      url: async (doc: any, context: any) => await resolveProductionUrl(doc, context),
      reload: {
        button: true,
      },
    })
    .title('Preview'),
]

// Default document node with views
export const getDefaultDocumentNode = (S: any, {schemaType}: {schemaType: string}) => {
  // Only add preview and SEO views for content documents that have pageInfo
  const documentsWithPageInfo = [
    'home',
    'wheelLines',
    'bestSellersPage',
    'sprintLine',
    'comingSoon',
    'taxRefund',
    'blackFriday',
    'memorialDay',
    'project',
    'gallery',
    'accessories',
    'cart',
    'contact',
    'fitmentGuidesHome',
    'promotions',
    'resourceCenter',
    'realPerformancePage',
    'replacementEligibility',
    'replacementWheel',
    'fitmentHelp',
    'getExpertAdvice',
    'wheelLanding',
    'fiftyReplacement',
    'fitmentGuide',
    'buyerGuide',
    'article',
    'accessory',
    'sku',
    'customerSupportArticle',
    'customerSupport',
    'resourceCenterArticle',
    'blogHome',
    'dynamicPages',
    'vehicles',
  ]

  if (documentsWithPageInfo.includes(schemaType)) {
    return S.document().views(defaultDocumentViews(S))
  }

  return S.document()
}

// Fitment tags that should be grouped together
const fitmentTags = ['cambersTag', 'spacersTag', 'fitmentTag']

const attributes = [
  'boltPattern',
  'camber',
  'centerBore',
  'centerCap',
  'centerCapSize',
  'bestSellers',
  'color',
  'design',
  'diameter',
  'diameterSet',
  'finish',
  'fitment',
  'constructionType',
  'line',
  'lug',
  'make',
  'model',
  'offset',
  'profile',
  'propertyRef',
  'requiredMod',
  'scoringCriteria',
  'spacer',
  'tireBrand',
  'tireModel',
  'tireNote',
  'tireSize',
  'tireTreadwear',
  'tireType',
  'width',
  'configuratorWarning',
]

const configPages = {
  settings: 'Settings',
}

const commonBlocks = {
  getExpertAdvice: 'Get Expert Advice',
  apexQualityStandard: 'Apex Quality Standard',
  fitmentHelp: 'Fitment Help',
  accessoriesBanners: 'Accessories Banners',
  settingsConfiguratorAccessories: 'Accessories Configurator',
  warrantyCommonBlock: 'Warranty',
}

const singlePages = {
  home: 'Home',
  contact: 'Contact',
  wheelLines: 'All Wheels',
  bestSellersPage: 'Best Sellers',
  sprintLine: 'Sprint Line',
  comingSoon: 'Coming Soon',
  taxRefund: 'Tax Refund',
  blackFriday: 'Black Friday',
  memorialDay: 'Memorial Day Sale',
  project: 'Project ml-10',
  gallery: 'Gallery',
  cart: 'Cart',
  vehicles: 'Vehicles',
  promotions: 'Promotions',
  realPerformancePage: 'Real Performance',
}

const accessoryPages = {
  singlePages: {
    accessories: 'Accessories Home',
  },
  multiple: ['accessory', 'sku'],
}

const replacementProgramPages = {
  fiftyReplacement: 'Replacement Program',
  replacementEligibility: 'Replacement Eligibility',
  replacementWheel: 'Choose Replacement Wheel',
}

const blogPages = ['blogHome', 'article', 'articleCategory', 'author']
const fitmentGuidesPages = ['fitmentGuidesHome', 'fitmentGuide']
const buyerGuidesPages = ['buyerGuide', 'aiGenerationRules', 'buyerGuidesTemplate']
const customerSupportPages = ['customerSupport', 'customerSupportArticle']
const resourceCenterPages = ['resourceCenter', 'resourceCenterArticle']

const customPages = ['wheelLanding', 'dynamicPages']
const navs = ['mainNav', 'footerNav']

const hide = [
  'model',
  'generation',
  'submodel',
  'commonPageBuilder',
  'configuration',
  'configuratorWarning',
  'accessoryConfiguration',
  'fitmentFilter',
  'fitmentGuidesHome',
  'fitmentGuide',
  'buyerGuide',
  'aiGenerationRules',
  'buyerGuidesTemplate',
  'configurationAccessories',
  'centeringRingsConfiguration',
  'tpmsStemsConfiguration',
  'centerCapsConfiguration',
  'spacersConfiguration',
  'lugBoltsStudsConfiguration',
  'modal',
  'media.tag',
  'vehicle',
  'customerSupport',
  'customerSupportArticle',
  'resourceCenter',
  'resourceCenterArticle',
  'realPerformancePage',
  'infoBar',
  'promotions',
  'promotion',
  'redirect',
]

const internalPages = [
  ...Object.keys(singlePages),
  ...Object.keys(accessoryPages.singlePages),
  ...accessoryPages.multiple,
  ...Object.keys(replacementProgramPages),
  ...blogPages,
  ...customPages,
  ...fitmentGuidesPages,
  ...buyerGuidesPages,
  ...customerSupportPages,
  ...resourceCenterPages,
  'constructionType',
]

export {internalPages}

export const structure: StructureResolver = (S, context) =>
  S.list()
    .title('Content')
    .items([
      S.listItem()
        .title('Pages')
        .child(
          S.list()
            .title('Pages')
            .items([
              // Loop through singlePages
              ...Object.keys(singlePages).map((k) =>
                S.listItem()
                  .title(singlePages[k as keyof typeof singlePages])
                  .child(
                    S.document()
                      .schemaType(k)
                      .documentId(`singleton-${k}`)
                      .views(defaultDocumentViews(S))
                  ),
              ),
              ...S.documentTypeListItems().filter((i) => [...customPages].includes(i.getId()!)),
              S.listItem()
                .title('Customer Support')
                .child(
                  S.list()
                    .title('Customer Support')
                    .items([
                      S.listItem()
                        .title('Customer Support Home')
                        .child(
                          S.document()
                            .title('Customer Support Home')
                            .id('customerSupport')
                            .schemaType('customerSupport')
                            .documentId('singleton-customerSupport')
                            .views(defaultDocumentViews(S))
                        ),
                      S.divider(),
                      S.listItem()
                        .title('Customer Support Articles')
                        .child(
                          S.documentTypeList('customerSupportArticle').title(
                            'Customer Support Articles',
                          ),
                        ),
                    ]),
                ),
              S.listItem()
                .title('Resource Center')
                .child(
                  S.list()
                    .title('Resource Center')
                    .items([
                      S.listItem()
                        .title('Resource Center Home')
                        .child(
                          S.document()
                            .title('Resource Center Home')
                            .id('resourceCenter')
                            .schemaType('resourceCenter')
                            .documentId('singleton-resourceCenter')
                            .views(defaultDocumentViews(S))
                        ),
                      S.divider(),
                      S.listItem()
                        .title('Resource Center Articles')
                        .child(
                          S.documentTypeList('resourceCenterArticle').title(
                            'Resource Center Articles',
                          ),
                        ),
                    ]),
                ),
              S.listItem()
                .title('Accessory Pages')
                .child(
                  S.list()
                    .title('Accessory Pages')
                    .items([
                      ...Object.keys(accessoryPages.singlePages).map((k) =>
                        S.listItem()
                          .title(accessoryPages.singlePages[k as keyof typeof accessoryPages.singlePages])
                          .child(
                            S.document()
                              .schemaType(k)
                              .documentId(`singleton-${k}`)
                              .views(defaultDocumentViews(S))
                          ),
                      ),
                      ...S.documentTypeListItems().filter((i) =>
                        [...accessoryPages.multiple].includes(i.getId()!),
                      ),
                    ]),
                ),
              S.listItem()
                .title('Replacement Program Pages')
                .child(
                  S.list()
                    .title('Replacement Program Pages')
                    .items([
                      ...Object.keys(replacementProgramPages).map((k) =>
                        S.listItem()
                          .title(replacementProgramPages[k as keyof typeof replacementProgramPages])
                          .child(
                            S.document()
                              .schemaType(k)
                              .documentId(`singleton-${k}`)
                              .views(defaultDocumentViews(S))
                          ),
                      ),
                    ]),
                ),
            ]),
        ),
      ...S.documentTypeListItems().filter((i) => ['make'].includes(i.getId()!)),
      ...S.documentTypeListItems().filter(
        (i) =>
          ![
            ...navs,
            ...customPages,
            ...hide,
            ...Object.keys(singlePages),
            ...Object.keys(accessoryPages.singlePages),
            ...accessoryPages.multiple,
            ...Object.keys(replacementProgramPages),
            ...Object.keys(configPages),
            ...Object.keys(commonBlocks),
            ...blogPages,
            ...attributes,
            ...fitmentTags,
          ].includes(i.getId()!),
      ),

      S.listItem()
        .title('Navigation')
        .child(
          S.list()
            .title('Navs')
            .items([
              S.listItem()
                .title('Main')
                .child(
                  S.editor().id('mainNav').schemaType('mainNav').documentId('singleton-mainNav'),
                ),
              S.listItem()
                .title('Footer')
                .child(
                  S.editor()
                    .id('footerNav')
                    .schemaType('footerNav')
                    .documentId('singleton-footerNav'),
                ),
              S.listItem()
                .title('Info bar')
                .child(
                  S.editor().id('infoBar').schemaType('infoBar').documentId('singleton-infoBar'),
                ),
            ]),
        ),

      S.listItem()
        .title('Blog')
        .child(
          S.list()
            .title('Blog')
            .items([
              S.listItem()
                .title('Blog Home')
                .child(
                  S.editor()
                    .title('Blog Home')
                    .id('blogHome')
                    .schemaType('blogHome')
                    .documentId('singleton-blogHome'),
                ),
              S.listItem().title('Articles').child(S.documentTypeList('article').title('Articles')),
              S.listItem()
                .title('Categories')
                .child(S.documentTypeList('articleCategory').title('Categories')),
              S.listItem().title('Authors').child(S.documentTypeList('author').title('Authors')),
            ]),
        ),

      S.listItem()
        .title('Fitment guides')
        .child(async () => {
          const client = context.getClient({apiVersion: '2023-05-03'})
          const makes = await client.fetch(
            `*[_type == 'make' && !(_id in path('drafts.**'))] | order(title asc)`,
          )

          return S.list()
            .title('Fitment guides')
            .items([
              S.listItem()
                .title('Fitment Guides Home')
                .child(
                  S.editor()
                    .title('Fitment Guides Home')
                    .id('fitmentGuidesHome')
                    .schemaType('fitmentGuidesHome')
                    .documentId('singleton-fitmentGuidesHome'),
                ),
              S.divider(),
              S.listItem()
                .title('All fitment guides')
                .child(S.documentTypeList('fitmentGuide').title('All fitment guides')),
              S.listItem()
                .title('Unassigned')
                .child(
                  S.documentTypeList('fitmentGuide')
                    .title(`Unassigned fitment guides`)
                    .filter(
                      `_type == "fitmentGuide" && !(_id in path("drafts.**")) && count(*[_type == 'generation' && references(^._id)]) == 0`,
                    ),
                ),
              S.divider(),
              ...makes.map((make: any) =>
                S.listItem()
                  .title(make.name)
                  .child(async () => {
                    // get fitmentGuideIDs related to specific make
                    const fitmentGuides = await client.fetch(
                      `*[_type == 'make' && _id == $makeId].models[]->generations[]->fitmentGuide->_id`,
                      {
                        makeId: make._id,
                      },
                    )

                    return S.documentTypeList('fitmentGuide')
                      .title(`${make.name} fitment guides`)
                      .filter(`_type == "fitmentGuide" && _id in $fitmentGuides`)
                      .params({fitmentGuides})
                  }),
              ),
            ])
        }),

      S.listItem()
        .title('Buyer Guides')
        .child(async () => {
          const client = context.getClient({apiVersion: '2023-05-03'})
          const makes = await client.fetch(
            `*[_type == 'make' && !(_id in path('drafts.**'))] | order(name asc)`,
          )

          return S.list()
            .title('Buyer Guides')
            .items([
              S.listItem()
                .title('Buyer Guides Settings')
                .child(
                  S.editor()
                    .title('Buyer Guides Settings')
                    .id('buyerGuidesTemplate')
                    .schemaType('buyerGuidesTemplate')
                    .documentId('singleton-buyerGuidesTemplate'),
                ),
              S.listItem()
                .title('AI Generation Rules')
                .child(
                  S.documentTypeList('aiGenerationRules')
                    .title('AI Generation Rules')
                    .defaultOrdering([{field: 'isActive', direction: 'desc'}, {field: '_createdAt', direction: 'desc'}]),
                ),
              S.divider(),
              S.listItem()
                .title('All buyer guides')
                .child(S.documentTypeList('buyerGuide').title('All buyer guides')),
              S.listItem()
                .title('Unassigned')
                .child(
                  S.documentTypeList('buyerGuide')
                    .title(`Unassigned buyer guides`)
                    .filter(
                      `_type == "buyerGuide" && !(_id in path("drafts.**")) && count(*[_type == 'generation' && references(^._id)]) == 0`,
                    ),
                ),
              S.divider(),
              ...makes.map((make: any) =>
                S.listItem()
                  .title(make.name)
                  .child(async () => {
                    // get buyerGuide IDs related to specific make through generations
                    const buyerGuides = await client.fetch(
                      `*[_type == 'make' && _id == $makeId].models[]->generations[]->buyerGuide->_id`,
                      {
                        makeId: make._id,
                      },
                    )

                    return S.documentTypeList('buyerGuide')
                      .title(`${make.name} buyer guides`)
                      .filter(`_type == "buyerGuide" && _id in $buyerGuides`)
                      .params({buyerGuides})
                  }),
              ),
            ])
        }),

      S.listItem()
        .title('Accessories Configurators')
        .child(
          S.documentTypeList('configurationAccessories')
            .title('Accessories Configurators')
            .defaultOrdering([{field: 'name', direction: 'asc'}]),
        ),

      S.divider(),

      S.listItem().title('Promotions').child(S.documentTypeList('promotion').title('Promotions')),

      S.listItem().title('Redirects').child(S.documentTypeList('redirect').title('Redirects')),

      S.divider(),

      S.listItem()
        .title('Attributes')
        .child(
          S.list()
            .title('Attributes')
            .items([
              S.listItem()
                .title('Media Tags')
                .icon(RiImageAddLine)
                .child(S.documentTypeList('media.tag').title('Media Tags')),
              S.listItem()
                .title('Fitment Tags')
                .child(
                  S.list()
                    .title('Fitment Tags')
                    .items([
                      S.listItem()
                        .title('Camber Tag')
                        .child(
                          S.editor()
                            .id('cambersTag')
                            .schemaType('cambersTag')
                            .documentId('cambersTag'),
                        ),
                      S.listItem()
                        .title('Spacers Tag')
                        .child(
                          S.editor()
                            .id('spacersTag')
                            .schemaType('spacersTag')
                            .documentId('spacersTag'),
                        ),
                      S.divider(),
                      // documentTypeList exposes the "+" create button (and respects
                      // the schema orderings). The previous hand-mapped list only
                      // linked existing docs, so new tags couldn't be created — a
                      // regression from the Sanity v2 -> v4 migration.
                      S.listItem()
                        .title('All Fitment Tags')
                        .child(
                          S.documentTypeList('fitmentTag').title('Fitment Tags'),
                        ),
                    ]),
                ),
              ...S.documentTypeListItems().filter((i) => attributes.includes(i.getId()!)),
            ]),
        ),

      S.listItem()
        .title('Common Blocks')
        .child(
          S.list()
            .title('Common Blocks')
            .items([
              ...Object.keys(commonBlocks).map((k) =>
                S.listItem()
                  .title(commonBlocks[k as keyof typeof commonBlocks])
                  .child(
                    // Common blocks are reusable, globally-embedded blocks with
                    // no dedicated frontend URL, so there is nothing to preview.
                    // Force form-only views (no Preview tab). This overrides the
                    // Preview view that getDefaultDocumentNode would otherwise add
                    // for the types listed in documentsWithPageInfo.
                    S.document()
                      .schemaType(k)
                      .documentId(`singleton-${k}`)
                      .views([S.view.form()])
                  ),
              ),
              S.listItem()
                .title('Common Page builder')
                .child(S.documentTypeList('commonPageBuilder').title(`Common Page builder`)),
            ]),
        ),

      S.listItem()
        .title('Settings')
        .child(S.document().schemaType('settings').documentId('singleton-settings')),
    ])
